'use client';

/**
 * PaymentMethodCard Component
 * Reusable component for displaying payment method information
 *
 * Design: Matches Figma "Spending limit cell" pattern
 * - Card icon on the left (80x52, gray background, 6px radius)
 * - Label + subtext in the middle
 * - Arrow icon on the right (when clickable)
 */

import React from 'react';
import cn from 'classnames';
import type { PaymentMethod } from '../types';
import { PURCHASE_FLOW_ICONS, PURCHASE_FLOW_TEXT_KEYS } from '../utils/constants';
import { getPaymentMethodBrandLabel, maskEmailForDisplay } from '../utils/displayUtils';
import { usePurchaseRender } from '../config/PurchaseFlowConfigProvider';
import classes from './classes.module.scss';

/** Theme variant: default (compact) or wallet (larger on desktop for payment sources page) */
export type PaymentMethodCardThemeVariant = 'default' | 'wallet';

interface PaymentMethodCardProps {
  paymentMethod: PaymentMethod;
  onClick?: () => void;
  showDefaultIndicator?: boolean;
  showError?: boolean;
  showArrow?: boolean;
  highlightDefault?: boolean;
  /** PAYG ineligibility message — shown inside the card with a muted background */
  paygIneligibleMessage?: string;
  /** Wallet variant: larger font/sizing on desktop (payment sources page) */
  themeVariant?: PaymentMethodCardThemeVariant;
  className?: string;
}

/**
 * Check if payment method has any error state
 */
export const hasPaymentMethodError = (method: PaymentMethod): boolean => !!(method.error || method.expired || method.reauthorizeRequired);

/**
 * Get display error message for payment method.
 * Prefers the API-provided error text (method.error) when available,
 * falling back to generic labels for expired / reauthorize states.
 */
export const getPaymentMethodErrorMessage = (method: PaymentMethod): string | null => {
  if (method.error) {
    return method.error;
  }
  if (method.expired) {
    return 'Card expired';
  }
  if (method.reauthorizeRequired) {
    return 'Re-authorization required';
  }
  return null;
};

/**
 * Get the card type string for icon lookup.
 * Maps from PaymentMethod kind/label to an icon identifier.
 * Uses PURCHASE_FLOW_ICONS constants for known types.
 */
export const getCardType = (paymentMethod: PaymentMethod): string => {
  switch (paymentMethod.kind) {
    case 'cc':
      return paymentMethod.label || PURCHASE_FLOW_ICONS.UNKNOWN_CARD;
    case 'pp':
      return PURCHASE_FLOW_ICONS.PAYPAL;
    case 'ap':
    case 'am':
      return PURCHASE_FLOW_ICONS.APPLE_PAY;
    case 'gp':
      return PURCHASE_FLOW_ICONS.GOOGLE_PAY;
    case 'iap':
      return PURCHASE_FLOW_ICONS.IAP;
    default:
      return PURCHASE_FLOW_ICONS.UNKNOWN_CARD;
  }
};

/** Display label for list card (brand + last4 for cc, else brand only) */
const getDisplayLabel = (paymentMethod: PaymentMethod): string => {
  if (paymentMethod.kind === 'cc') {
    return `${ getPaymentMethodBrandLabel(paymentMethod) } * * * ${ paymentMethod.last4 || '' }`;
  }
  return getPaymentMethodBrandLabel(paymentMethod);
};

/**
 * Get the subtext for the payment method.
 * Accepts renderText to translate text keys (e.g. default payment indicator).
 */
const getSubtext = (
  paymentMethod: PaymentMethod,
  showDefaultIndicator: boolean,
  hasError: boolean,
  renderText: (key: string) => React.ReactNode
): React.ReactNode | null => {
  if (hasError) return null;

  switch (paymentMethod.kind) {
    case 'pp': {
      const masked = paymentMethod.email ? maskEmailForDisplay(paymentMethod.email) : null;
      return masked;
    }
    default:
      if (showDefaultIndicator && paymentMethod.isDefault) {
        return renderText(PURCHASE_FLOW_TEXT_KEYS.DEFAULT_PAYMENT_METHOD);
      }
      return null;
  }
};

export const PaymentMethodCard: React.FC<PaymentMethodCardProps> = ({
  paymentMethod,
  onClick,
  showDefaultIndicator = true,
  showError = true,
  showArrow = true,
  highlightDefault = false,
  paygIneligibleMessage,
  themeVariant = 'default',
  className
}) => {
  const { renderIcon, renderText } = usePurchaseRender();
  const hasError = hasPaymentMethodError(paymentMethod);
  const errorMessage = getPaymentMethodErrorMessage(paymentMethod);
  const cardType = getCardType(paymentMethod);
  const displayLabel = getDisplayLabel(paymentMethod);
  const showPaygWarning = !!paygIneligibleMessage && !hasError;
  const subtext = getSubtext(paymentMethod, showDefaultIndicator, (hasError && showError) || showPaygWarning, renderText);

  const isHighlighted = highlightDefault && paymentMethod.isDefault && !(showError && hasError);

  const cardClassName = cn(
    classes.paymentMethodCard,
    {
      [classes.paymentMethodCardError]: showError && hasError,
      [classes.paymentMethodCardPaygIneligible]: showPaygWarning && highlightDefault,
      [classes.paymentMethodCardDefault]: isHighlighted,
      [classes.paymentMethodCardWallet]: themeVariant === 'wallet'
    },
    className
  );

  const content = (
    <>
      {/* Error indicator icon (red circle — matches old renderErrorImg) */}
      {showError && hasError && (
        <div className={ classes.sourceErrorContainer }>
          {renderIcon(PURCHASE_FLOW_ICONS.PAYMENT_SOURCE_ERROR)}
        </div>
      )}

      {/* Source name container (icon + text) */}
      <div className={ classes.sourceNameContainer }>
        <div className={ classes.paymentMethodIconContainer }>
          {renderIcon(cardType)}
        </div>
        <div className={ classes.paymentMethodTextContainer }>
          <div className={ classes.paymentMethodLabel }>
            {' '}
            {displayLabel}
          </div>
          {subtext && (
            <div className={ classes.paymentMethodSubtext }>{subtext}</div>
          )}
          {showError && errorMessage && (
            <div className={ classes.paymentMethodErrorText }>{errorMessage}</div>
          )}
          {showPaygWarning && (
            <div className={ classes.paymentMethodErrorText }>{paygIneligibleMessage}</div>
          )}
        </div>
      </div>

      {/* Arrow icon */}
      {showArrow && onClick && (
        <div className={ classes.paymentMethodArrow }>
          {renderIcon(PURCHASE_FLOW_ICONS.ARROW_RIGHT)}
        </div>
      )}
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={ onClick }
        className={ cardClassName }
        data-testid={ `payment-method-card-${ paymentMethod.kind }` }
      >
        {content}
      </button>
    );
  }

  return <div className={ cardClassName } data-testid={ `payment-method-card-${ paymentMethod.kind }` }>{content}</div>;
};
